import { useCallback, useEffect, useRef, useState } from 'react';
import { generateBeArtifact, getBeArtifact } from '../../Requests/BehavioralEconomicsRequests';

/**
 * Shared fetch/generate/poll logic for the three AI-generated Behavioral Economics
 * artifacts (be_biases, be_report, be_insights). Fetches once on mount, and while the
 * server reports "processing" it polls every few seconds until completed/failed.
 *
 * Returns: { status, result, notes, loading, error, generate }
 * status: not_started | processing | completed | failed
 */
export function useBehavioralArtifact(projectUuid, analysisUuid, type) {
    const [status, setStatus] = useState('not_started');
    const [result, setResult] = useState(null);
    const [notes, setNotes] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const pollRef = useRef(null);

    const fetchOnce = useCallback(async () => {
        try {
            const res = await getBeArtifact(projectUuid, analysisUuid, type);
            setStatus(res.data.status);
            setResult(res.data.result);
            setNotes(res.data.notes ?? null);
            return res.data.status;
        } catch (e) {
            setError(e);
            return 'failed';
        }
    }, [projectUuid, analysisUuid, type]);

    // Initial load.
    useEffect(() => {
        let active = true;
        (async () => {
            const s = await fetchOnce();
            if (active && s === 'processing') startPolling();
            if (active) setLoading(false);
        })();
        return () => {
            active = false;
            if (pollRef.current) clearInterval(pollRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchOnce]);

    const startPolling = useCallback(() => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            const s = await fetchOnce();
            if (s !== 'processing' && pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        }, 4000);
    }, [fetchOnce]);

    const generate = useCallback(async () => {
        setError(null);
        setStatus('processing');
        setResult(null);
        try {
            await generateBeArtifact(projectUuid, analysisUuid, type);
            startPolling();
        } catch (e) {
            setError(e);
            setStatus('failed');
        }
    }, [projectUuid, analysisUuid, type, startPolling]);

    return { status, result, notes, loading, error, generate };
}
